import type { FunctionComponent, ReactNode } from 'react';
import CheckCircleOutlineIcon from '@atlaskit/icon/core/check-circle';
import { cssMap } from '@atlaskit/css';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

export interface PermissionCardProps {
  /** Bold title shown at the top of the card. */
  title: ReactNode;
  /** Description shown below the title. */
  description: ReactNode;
}

const cardStyle = xcss({
  borderRadius: 'radius.xxlarge',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  padding: 'space.200',
});

const iconSpacingStyles = cssMap({
  space050: {
    display: 'inline-flex',
    paddingBlock: token('space.050'),
    paddingInline: token('space.050'),
  },
});

/**
 * A read-only permission card with a check-circle icon, bold title, and description.
 * Shared by A2A and MCP (view mode) consent flows.
 */
const PermissionCard: FunctionComponent<PermissionCardProps> = ({
  title,
  description,
}) => {
  return (
    <Box xcss={cardStyle}>
      <Flex gap="space.150" alignItems="start">
        <Box xcss={iconSpacingStyles.space050} aria-hidden>
          <CheckCircleOutlineIcon
            label=""
            size="medium"
            color={token('color.text')}
          />
        </Box>
        <Stack space="space.100">
          <Text weight="bold">{title}</Text>
          <Text as="p" size="medium">
            {description}
          </Text>
        </Stack>
      </Flex>
    </Box>
  );
};

export default PermissionCard;
